// store global types
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types

import type { TApiAuth } from "@/apis/auth/apiAuth";

import type {
    TApiThirdParty,
    TErrorGetThirdPartyAuth0Config,
    TResponseGetThirdPartyAuth0Config,
} from "@/apis/apiThirdParty/apiThirdParty";

type TStateThirdPartyAuth = {
    accessToken: string | null;
    isThirdPartyAuthenticated: boolean;
    thirdPartyTokenState: TAsyncState<string, Error>;
    thirdPartyTokenAvailableState: "idle" | "pending" | "resolved" | "rejected";
    thirdPartyAuthConfig: TAsyncState<
        TResponseGetThirdPartyAuth0Config,
        TErrorGetThirdPartyAuth0Config
    >;
    onInitializeSuccess: (() => void) | null;
};
type TActionsPublicThirdPartyAuth = {
    initialiseThirdPartyAuth: () => Promise<void>;
    getThirdPartyTokenOrRedirectToLogin: (params?: {
        returnTo: string;
    }) => Promise<string | null>;
    getUser: () => Promise<void>;
    logoutThirdParty: () => Promise<void>;
    ensureTokenIsAvaiable: () => Promise<void>;
};

export type TSliceThirdPartyAuth = TStateThirdPartyAuth &
    TActionsPublicThirdPartyAuth;

type TSliceDependenciesThirdPartyAuth = {
    apiThirdPartyAuth: TApiAuth;
    apiThirdParty: TApiThirdParty;
    navigateToReferer: ({ url }: { url: string | null }) => void;
    thirdPartyDocUrl: string;
};

export const initialStateThirdPartyAuth = {
    accessToken: null,
    isThirdPartyAuthenticated: false,
    thirdPartyTokenState: {
        status: "idle",
        data: null,
        error: null,
    },
    thirdPartyAuthConfig: {
        status: "idle",
        data: null,
        error: null,
    },
    thirdPartyTokenAvailableState: "idle",
    onInitializeSuccess: null,
} as const satisfies TStateThirdPartyAuth;

export const createSliceThirdPartyAuth: TCreateSlice<
    TSliceThirdPartyAuth,
    TSliceDependenciesThirdPartyAuth
> =
    ({ apiThirdPartyAuth, apiThirdParty, thirdPartyDocUrl }) =>
    (set, get) => {
        const actionsPublic: TActionsPublicThirdPartyAuth = {
            logoutThirdParty: async () => {
                await apiThirdPartyAuth.logout();
                set({
                    accessToken: null,
                    isThirdPartyAuthenticated: false,
                });
            },
            getUser: async () => {
                const user = await apiThirdPartyAuth.getUser();
                if (user) {
                    set({ isThirdPartyAuthenticated: true });
                } else {
                    set({
                        accessToken: null,
                        isThirdPartyAuthenticated: false,
                    });
                }
            },
            initialiseThirdPartyAuth: async () => {
                set({
                    thirdPartyAuthConfig: {
                        status: "pending",
                        data: null,
                        error: null,
                    },
                });
                try {
                    const response =
                        await apiThirdParty.getApiThirdPartyAuth0Config();
                    const config = response.data;
                    await apiThirdPartyAuth.initialize(config);
                    const callback = get().onInitializeSuccess;
                    if (callback) {
                        callback();
                    }
                    set(
                        {
                            thirdPartyAuthConfig: {
                                status: "resolved",
                                data: config,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartyauth/initialiseThirdPartyAuth/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            thirdPartyAuthConfig: {
                                status: "rejected",
                                data: null,
                                error,
                            },
                        },
                        false,
                        "thirdpartyauth/initialiseThirdPartyAuth/rejected",
                    );
                }
            },
            ensureTokenIsAvaiable: async () => {
                set({
                    thirdPartyTokenAvailableState: "pending",
                });
                try {
                    await apiThirdPartyAuth.getTokenSilently();
                    set({
                        thirdPartyTokenAvailableState: "resolved",
                    });
                } catch (error) {
                    const authError = error as { error: string };
                    set({
                        thirdPartyTokenAvailableState: "rejected",
                    });
                    if (
                        authError.error === "missing_refresh_token" ||
                        authError.error === "invalid_grant"
                    ) {
                        console.error(
                            "Token not available, redirecting to login...",
                        );
                        await apiThirdPartyAuth.loginWithRedirect();
                    } else {
                        console.error(
                            "Unexpected error fetching token:",
                            error,
                        );
                    }
                }
            },
            getThirdPartyTokenOrRedirectToLogin: async (
                { returnTo } = { returnTo: thirdPartyDocUrl },
            ) => {
                set(
                    {
                        thirdPartyTokenState: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                        accessToken: null,
                    },
                    false,
                    "thirdpartyauth/getThirdPartyTokenOrRedirectToLogin/pending",
                );

                try {
                    const token = await apiThirdPartyAuth.getTokenSilently();
                    set(
                        {
                            thirdPartyTokenState: {
                                status: "resolved",
                                data: token,
                                error: null,
                            },
                            accessToken: token,
                        },
                        false,
                        "thirdpartyauth/getThirdPartyTokenOrRedirectToLogin/resolved",
                    );
                    return token;
                } catch (error) {
                    const authError = error as { error: string };
                    if (
                        authError.error === "missing_refresh_token" ||
                        authError.error === "invalid_grant"
                    ) {
                        console.error(
                            "Token refresh failed, redirecting to login page...",
                        );

                        await apiThirdPartyAuth.loginWithRedirect(returnTo);
                    } else {
                        const currentError = error as Error;
                        set(
                            {
                                thirdPartyTokenState: {
                                    status: "rejected",
                                    data: null,
                                    error: currentError,
                                },
                                accessToken: null,
                            },
                            false,
                            "thirdpartyauth/getThirdPartyTokenOrRedirectToLogin/rejected",
                        );
                    }
                    return null;
                }
            },
        };

        return {
            ...initialStateThirdPartyAuth,
            ...actionsPublic,
        };
    };
